import React, { useEffect, useState } from 'react';
import { getPlansMeta, updatePlan } from '../../../../services/CompaniesManagement';
import { InputDinamic, SelectConfig, SelectItem, ToggleConfig, AdsComponent, CustomColors } from '../components';
import { arrayMove } from '@dnd-kit/sortable';
import { ErrorMessage, SuccessMessage } from '../../../../utils/alerts';
import { useTranslation } from 'react-i18next';
import { useParams } from "react-router-dom";

export const usePlan = () => {
  const [planData, setPlanData] = useState([]);
  const [planMeta, setPlanMeta] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isPlanLoading, setIsPlanLoading] = useState(false);
  const { t } = useTranslation("common");
  const { businessId } = useParams();
  
  const getPlanData = async () => {
    setIsPlanLoading(true);
    const data = await getPlansMeta(businessId);
    setPlanData(data.categories);
    setPlanMeta(data.categories);
    setIsPlanLoading(false);
  };

  const onChange = (e, data) => {
    setPlanData(state => {
      // atualização do item alterado
      const updatedState = state.map(item => {
        if (item.key === e.target.name || item.key === e.target.id ) {
          if (item.type === "bool") {
            return { ...item, value: Boolean(item.value) == 1 ? 0 : 1 }
          }

          if (item.type === "string" && item?.options?.multiple) {
            const values = item?.value?.split(",").filter(Boolean) ?? [];

            if (values?.includes(String(data?.value))) {
              return { ...item, value: values?.filter(val => val !== String(data?.value)).join(",") }
            }

            values.push(data.value);
            
            if (values?.length === item?.options?.list?.length) return { ...item, value: "" };

            return { ...item, value: values.join(",") }
          }

          return { ...item, value: e.target.value }
        }

        return item;
      });

      // atualização dos componentes que têm parent_key igual à key do item que foi alterado
      let finalState = updatedState.map(item => {
        if (item?.parent_key) {
          const parent = updatedState.find(parent => parent.key === item.parent_key);
          if (parent && parent.type === "bool" && !Boolean(parent.value) && item.type === "bool") {
            return { ...item, value: 0 }
          }
        }

        return item;
      });

      const changedItemKey = e.target.name || e.target.id;
      const changedItem = updatedState.find(item => item.key === changedItemKey);

      // quando tele_category_enabled muda e Nutricionista (1) é removido, desativa nutri360_enabled
      if (changedItemKey === "tele_category_enabled") {
        if (!changedItem?.value?.split(",").includes("1")) {
          finalState = finalState.map(item =>
            item.key === "nutri360_enabled" ? { ...item, value: 0 } : item
          );
        }
      }

      // quando nutri360_enabled é ativado, habilita professional_enabled e inclui Nutricionista nas categorias
      if (changedItemKey === "nutri360_enabled" && Boolean(changedItem?.value)) {
        finalState = finalState.map(item => {
          if (item.key === "professional_enabled" && !Boolean(item.value)) {
            return { ...item, value: 1 };
          }
          if (item.key === "tele_category_enabled") {
            if (!item?.value?.split(",").includes("1")) {
              const values = item?.value?.split(",").filter(Boolean) ?? [];
              return { ...item, value: [...values, "1"].join(",") };
            }
          }
          return item;
        });
      }

      // atualiza menu quando algum dos itens do menu for desativado
      if (changedItem && changedItem.type === "bool" && 
        [
          "telemedicine_enabled", 
          "professional_enabled", 
          "mood_enabled",
          "gym_enabled",
          "healthscan_enabled",
          "platescan_enabled",
          "chat_enabled",
          "sipat_enabled",
          "namuchef_enabled"
        ].includes(changedItemKey) && !Boolean(changedItem.value)) {
          const planMenu = finalState.find(item => item.key === "custom_menu");
          if (Object.keys(planMenu.value).includes(changedItemKey)) {
            finalState = finalState.map(item => {
              if (item.key === "custom_menu") {
                const newMenu = Object.fromEntries(Object.entries(item.value).filter(([key, value]) => key !== changedItemKey));
                return {
                  ...item,
                  value: newMenu
                };
              };
              
              return item;
            })
          }

          if (
            ["telemedicine_enabled", "professional_enabled"].includes(changedItemKey) 
            && Object.keys(planMenu.value).includes("tele_enabled")
            && finalState.find(i => (i.key === "telemedicine_enabled") && !Boolean(i.value))
            && finalState.find(i => (i.key === "professional_enabled") && !Boolean(i.value))
          ) {
            finalState = finalState.map(item => {
              if (item.key === "custom_menu") {
                const newMenu = Object.fromEntries(Object.entries(item.value).filter(([key, value]) => value !== 5));
                return {
                  ...item,
                  value: {
                    ...newMenu,
                    "profile": 5,
                  }
                }
              }

              return item;
            });
          };

          if (changedItemKey === "gym_enabled" && Object.keys(planMenu.value).includes("gym_enabled")) {
            finalState = finalState.map(item => {
              if (item.key === "custom_menu") {
                const newMenu = Object.fromEntries(Object.entries(item.value).filter(([key, value]) => value !== 4));
                return {
                  ...item,
                  value: {
                    ...newMenu,
                    "schedule": 4,
                  }
                }
              }

              return item;
            });
          }

          finalState = finalState.map(item => {
            if (Object.keys(planMenu.value).includes(changedItemKey)) {
              if (item.key === "custom_menu") {
                const newMenu = Object.fromEntries(Object.entries(item.value).filter(([key, value]) => key !== changedItemKey));
                return {
                  ...item,
                  value: {
                    ...newMenu,
                  }
                }
              }

              return item;
            }

            return item;
          });
      }

      return finalState;
    });
  };

  const onDragEnd = event => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setPlanData(state => state.map(item => {
        if (item.key === "ad_ids") {
          return {
            ...item,
            value: arrayMove(item.value, active.id, over.id)
          }
        }

        return item;
      }))
    }
  };

  const onChangeAdsTitle = (ad, newTitle) => {
    setPlanData(state => state.map(item => {
      if (item.key === "ad_ids") {
        item.value[ad] = {
          ...item.value[ad],
          title: newTitle
        };
        return item;
      }

      return item;
    }))
  };

  const onChanceColor = (primary, secondary) => {

    const obj = {};
    obj.primary_color = primary;
    obj.secondary_color = secondary;

    setPlanData(state => state.map(item => {
      if(item.key === "customized_properties" || item.key === "portal_theme"){
        return {
          ...item,
          value: JSON.stringify(obj)
        }
      }
      return item
    }));

  }

  const onDeleteAd = (ad_index) => {
    const planConfig = [...planData];
    const adIdsIndex = planData.findIndex(item => item.key === "ad_ids");
    planConfig[adIdsIndex].value.splice(ad_index, 1);
    setPlanData(planConfig);
  };

  const onUploadImage = (event) => {
    const localImageUrl = URL.createObjectURL(event.target.files[0]);
    setPlanData(state => state.map(item => {
      if (item.key === "ad_ids") {
        return {
          ...item,
          value: [
            ...item.value,
            {
              value: null,
              title: "",
              icon: localImageUrl,
              imageFile: event.target.files[0],
            }
          ]
        }
      };

      return item;
    }))
  };

  const getMenuFallback = (currentMenu, currentPlanData) => {
    const menuKeys = Object.keys(currentMenu);
    const disabledMenuKeys = menuKeys.filter(key => {
      if (key === 'tele_enabled') {
        const tele = currentPlanData.find(i => i.key === 'telemedicine_enabled');
        const prof = currentPlanData.find(i => i.key === 'professional_enabled');
        return !Boolean(tele?.value) && !Boolean(prof?.value);
      }
      const planItem = currentPlanData.find(i => i.key === key);
      return planItem && planItem.type === 'bool' && !Boolean(planItem.value);
    });

    if (!disabledMenuKeys.length) return currentMenu;

    const menuRelatedKeys = [
      'tele_enabled', 'sipat_enabled', 'mood_enabled', 'gym_enabled',
      'healthscan_enabled', 'platescan_enabled', 'chat_enabled', 'namuchef_enabled', 'nutri360_enabled',
      'blog', 'schedule', 'profile', 'lessons'
    ];

    const availableReplacements = menuRelatedKeys.filter(key => {
      if (menuKeys.includes(key)) return false;
      if (key === 'tele_enabled') {
        const tele = currentPlanData.find(i => i.key === 'telemedicine_enabled');
        const prof = currentPlanData.find(i => i.key === 'professional_enabled');
        return Boolean(tele?.value) || Boolean(prof?.value);
      }
      const planItem = currentPlanData.find(i => i.key === key);
      if (planItem && planItem.type === 'bool') return Boolean(planItem.value);
      // non-plan keys like blog, schedule, profile, lessons are always available
      return !planItem;
    });

    let newMenu = { ...currentMenu };
    disabledMenuKeys.forEach(disabledKey => {
      const position = newMenu[disabledKey];
      delete newMenu[disabledKey];
      const replacement = availableReplacements.find(k => !Object.keys(newMenu).includes(k));
      if (replacement) {
        newMenu[replacement] = position;
      }
    });

    return newMenu;
  };

  const onSave = async (e) => {
    setIsUpdating(true);
      try {
        e.preventDefault();
        const toUpdate = new FormData();
        planData.map(item => {
          if (item.key === "ad_ids") {
            const value = item.value.map(img => ({ id: img?.value ?? null, external_url: img?.title ?? "", image_url: !img?.imageFile ? img?.icon ?? "" : "" }));
            const hasNewImages = item?.value?.some(img => !!img?.imageFile);
            toUpdate.append("ads_data", JSON.stringify(value));
            if (hasNewImages) {
              for (let { imageFile } of item.value.filter(img => !!img?.imageFile)) {
                toUpdate.append("ads_images", imageFile);
              }
            };
          } else if (item.key === "custom_menu") {
            const fixedMenu = getMenuFallback(item.value, planData);
            toUpdate.append("custom_menu", JSON.stringify(fixedMenu));
          } else {
            if (item.value != null) toUpdate.append(item.key, item.value);
          }
        });
    
        const res = await updatePlan(businessId, toUpdate);

        if (res?.sucess) {
          SuccessMessage({ message: t("save_successfully") });
          setPlanMeta(planData);
        } else {
          ErrorMessage({ message: t("save_error") });
        }
    } catch (err) {
      ErrorMessage({ message: err.message });
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    getPlanData();
  }, []);

  const renderPlanList = () => {
    const usingConexa = planData.some(item => item.key === 'telemedicine_provider' && item.value === 'conexa');

    return planData?.length ? planData.map(plan => {
      if (plan.key === 'telemedicine_clinic' && !usingConexa) return null;

      if (
        plan.key === "psychosocial_mapping_end_date" ||
        plan.key === "psychosocial_mapping_start_date"
      ) {
        return null;
      };

      switch (plan?.type) {
        case "bool":
          return (
            <ToggleConfig
              isVisible={!!plan.visible}
              title={plan.name} 
              description={plan.description}
              name={plan.key}
              value={plan.value}
              onChange={onChange}
              canEdit={Boolean(plan.canEdit)}
              isEnabled={!plan.parent_key || Boolean(planData?.find?.(item => item.key === plan.parent_key)?.value)}
            />
          );

        case "int":
          return (
            <InputDinamic
              isVisible={!!plan.visible}
              title={plan.name}
              name={plan.key}
              id={plan.key}
              description={plan.description}
              type="number"
              value={plan.value || ""}
              onChange={onChange}
              canEdit={Boolean(plan.canEdit)}
              isEnabled={!plan.parent_key || Boolean(planData?.find?.(item => item.key === plan.parent_key)?.value)}
            /> 
          );

        case "string":
          if (plan?.options?.list?.length && !plan?.options?.multiple) {
            return (
              <SelectConfig 
                isVisible={!!plan.visible}
                title={plan.name}
                description={plan.description}
                options={plan.options.list}
                onChange={onChange}
                id={plan.key}
                name={plan.key}
                value={plan.value}
                canEdit={Boolean(plan.canEdit)}
                isEnabled={!plan.parent_key || Boolean(planData?.find?.(item => item.key === plan.parent_key)?.value)}
              />
            )
          }

          if (plan?.options?.multiple) {
            return (
              <SelectItem 
                isVisible={!!plan.visible}
                title={plan.name}
                description={plan.description}
                items={plan.options.list}
                onChange={onChange}
                name={plan.key}
                id={plan.key}
                value={plan.value}
                isEnabled={!plan.parent_key || Boolean(planData?.find?.(item => item.key === plan.parent_key)?.value)}
                parentConfig={planData?.find?.(item => item.key === plan.parent_key)}
              />
            )
          }

          if (plan?.options?.custom) {
            return (
              <AdsComponent
                isVisible={!!plan.visible}
                title={plan.name}
                value={plan.value}
                onDragEnd={onDragEnd}
                onChangeTitle={onChangeAdsTitle}
                onDeleteAd={onDeleteAd}
                onUploadImage={onUploadImage}
                isEnabled={!plan.parent_key || Boolean(planData?.find?.(item => item.key === plan.parent_key)?.value)}
              />
            )
          }

          if(plan?.key === 'customized_properties' || plan?.key === 'portal_theme'){
            return (
              <CustomColors
                isVisible={!!plan.visible}
                title={plan.name}
                name={plan.key}
                id={plan.key}
                value={plan.value || ""}
                onChange={onChanceColor}
                canEdit={Boolean(plan.canEdit)}
                isEnabled={!plan.parent_key || Boolean(planData?.find?.(item => item.key === plan.parent_key)?.value)}
              
              />
            )
          }

          return (
            <InputDinamic
              isVisible={!!plan.visible}
              title={plan.name}
              name={plan.key}
              id={plan.key}
              type={plan.key === "health_scan_notify_email" ? "email" :  "text"}
              description={plan.description}
              value={plan.value || ""}
              onChange={onChange}
              canEdit={Boolean(plan.canEdit)}
              isEnabled={
                !plan.parent_key || 
                Boolean(planData?.find?.(item => item.key === plan.parent_key)?.value) || 
                planData?.find?.(item => item.key === plan.parent_key)?.type !== "bool"
              }
            /> 
          )
          
        default:
          return <></>
      }
    }) : [];
  };

  return {
    renderPlanList,
    canSave: JSON.stringify(planData) !== JSON.stringify(planMeta),
    onSave,
    isUpdating,
    isPlanLoading,
    planMeta
  }
}
